import {
  get,
  groupBy,
  flatten,
  includes,
  values,
  without,
  maxBy,
  isNil,
  isEmpty,
} from 'lodash';
import {
  actionChannel,
  all,
  call,
  cancel,
  fork,
  put,
  race,
  select,
  take,
  takeEvery,
  takeLatest,
  delay,
} from 'typed-redux-saga/macro';
import { sagaIntl } from '@i18n';
import { modules } from '@veritone/glc-redux';
import { TDOId } from '@cbsa/state/modules/universal';
import { buffers } from 'redux-saga';
import { enqueueSnackbar } from '@common-modules/snackbar';
import {
  ROUTE_HOME,
  ROUTE_INGEST,
  ROUTE_INGEST_GOVQA,
  ROUTE_MEDIA_DETAILS,
  ROUTE_REDACTION_STATUSES,
  ROUTE_WATERMARKS,
  selectCurrentRoutePayload,
  selectPreviousRoute,
  selectRouteType,
} from '@common-modules/routing';
import { CREATE_TDO_SUCCESS } from '@redact-modules/uploads';
import { CREATE_ENGINE_JOB_SUCCESS } from '@common-modules/engines/ingestion';
import {
  FETCH_TDO_SUCCESS,
  IN_REDACTION_TAG_KEY,
} from '@common-modules/mediaDetails';
import {
  redactFileCompleted,
  redactingFile,
  selectRedactFiles,
} from '@common-modules/redactFile';
import {
  isRedactTaskComplete,
  isRedactTaskInFlight,
} from '@helpers/redactTaskStatus';
import { REDACTED_FILE_COMPLETED } from '@common-modules/mediaDetails/actions';
import { selectConfigEngines } from '@common-modules/engines/selectors';
import { updateStatusOnDetails } from '@common-modules/mediaDetails/services';
import * as Actions from '../actions';
import {
  selectHomeListStale,
  selectPagination,
  selectSDOById,
  selectSDOBySDOId,
  selectSDOsArray,
  selectSelectedTDOsArray,
  selectTDOById,
} from '../selectors';
import * as Services from '../services';
import { isSummaryTDORecord } from '../models';
import {
  updateOrgTdosJobsAction,
  updateOrgTdosTasksAction,
  FETCH_ORGANIZATION_TASKS_SUCCESS,
  FETCH_ORGANIZATION_TASKS_FAILURE,
  FETCH_ORGANIZATION_JOBS_SUCCESS,
  FETCH_ORGANIZATION_JOBS_FAILURE,
  actionUpdateStoreSoftDeletedTdos,
} from '../actions';
import caseMediaSaga from '@redact-state/modules/casemedia/sagas';
import {
  actionClearAllSelectedFolders,
  actionFetchFolderRequest,
} from '@redact-modules/casemedia/actions';
import {
  selectDefaultLimit,
  selectDefaultOffset,
} from '@redact-modules/casemedia/selectors';
import { selectEnableFolderView } from '@common/user-onboarding/selectors/organization';
import {
  resolveEditorReturnTarget,
  warmEditorReturnTargetTree,
} from '@redact-modules/casemedia/sagas/caseDiscovery';
import {
  watchDeleteRedactCodeSaga,
  watchRestoreRedactCodeSaga,
  watchSetRedactionCodeDeletedSaga,
  watchSoftDeleteRedactCodeSaga,
  watchSaveRedactionCodeRequestSaga,
  fetchCountRedactionCodes,
  watchCheckRedactionCodeExists,
} from '@redact-modules/redactionCodes/sagas';
import {
  waitForRedactionStatusesSettled,
  watchRedactionStatusAdministration,
  watchRedactionStatusSettings,
} from '@redact-modules/redactionStatuses/sagas';
import { actionFetchRedactionStatuses } from '@redact-modules/redactionStatuses/actions';
import { lexiconAdministrationSagas } from '@redact-modules/lexicons';
import { selectRedactionStatusSettings } from '@redact-modules/redactionStatuses/selectors';
import { getInitialWellKnownFileRedactionUpsertFields } from '@common-modules/mediaDetails/redactionStatuses';
// Deep imports (not the statusHistory barrel): the barrel pulls the sagas,
// which import redact-app modules — cycle risk (VE-26730).
import { actionRecordStatusHistory } from '@common-modules/statusHistory/actions';
import { deriveStatusFieldsFromTag } from '@common-modules/statusHistory/models';
import { notUndefined } from '@common/shared/util';
import defaultGlobalSettingsSaga from '@redact/state/modules/settingsProfile/sagas';
import watermarksSaga from '@redact-modules/watermarks/sagas';
import { actionFetchWatermarks } from '@redact-modules/watermarks/actions';
import { DetailTDO } from '@common-modules/mediaDetails/models';
import { selectTrashRetentionDays } from '@common/user-permissions';
import {
  watchRestoreActions,
  watchPermanentDeleteActions,
  watchTrashFolders,
  watchTrashTDOs,
  onActionFetchSDOsBulkByTDOIds,
  ensureTrashFolderId,
  runLoginTrashCleanup,
} from '@redact-modules/trash/sagas';
import {
  fileTdoWithCaseQuery,
  FileTdoWithCaseQueryResponse,
  moveTdoQuery,
  MoveTdoQueryResponse,
} from '@redact-modules/casemedia/services/queries';
import * as CaseMediaServices from '@redact-modules/casemedia/services';
import { isTdoFiledInTrash } from '@redact-modules/helper';
import { loadMediaDetailsPage } from '@common-modules/mediaDetails/saga';
// How many TDOs to fetch at a time.
export const POLLING_INTERVAL_MS = 60_000;

/**
 * Routes that actually display the org media list, its tasks and its jobs.
 *
 * initMainPage is shared with Redaction Codes, Statuses, Profiles and Trash, which show
 * none of that data. Since the saga is no longer restarted per navigation (VE-26729), its
 * pollers would otherwise keep hitting the org-wide SDO/TDO query and the two "very slow
 * when many TDOs in org" tasks/jobs queries the whole time the user sits on those pages —
 * and, for a cold deep link to one of them, forever.
 *
 * The pollers therefore SKIP a cycle when the route shows no media, rather than being
 * torn down and restarted. Restarting would refetch the whole list on the way back to
 * Home, which is the exact cost this ticket set out to remove; skipping keeps the store
 * intact so Home still renders instantly, and freshness resumes on the next cycle.
 */
export const MEDIA_LIST_ROUTE_TYPES: readonly string[] = [
  ROUTE_HOME.type,
  ROUTE_INGEST.type,
  ROUTE_INGEST_GOVQA.type,
];

export function* routeDisplaysMediaList() {
  const routeType = yield* select(selectRouteType);
  return MEDIA_LIST_ROUTE_TYPES.includes(routeType);
}

// this query is very slow when many TDOs in org - consider removing in future
export const TRACK_ORG_TASKS_CHANGES_INTERVAL = 20_000;
export const TRACK_ORG_JOBS_CHANGES_INTERVAL = 20_000;

const {
  user: { userIsAuthenticated, FETCH_USER_SUCCESS, LOGOUT },
} = modules;

// interface Action<P, M = unknown> {
//   type: string;
//   payload: P;
//   meta: M;
// }

/**
 * Kickoff watching actions and handling effects.
 *
 * Shared by the seven routes that set `preserveSagaOnRouteChange` in routes.ts: Home,
 * Settings Profile, Redaction Codes, Statuses, Ingest, Ingest/GovQA and Trash. The routing
 * saga keeps a route saga running across a navigation when both routes opt in and name the
 * same saga, so this runs once for a whole run of navigations among them instead of
 * restarting — and refetching the entire media list, the org tasks/jobs queries and the
 * folder tree — on each switch (VE-26729).
 *
 * The consequence: everything below runs on ENTRY TO THE GROUP, not entry to each page.
 * Anything a page needs fresh on its own entry belongs in that page's mount effect (see
 * RedactionCodes, SettingsProfile, Trash, RedactionStatuses), and anything invalidated
 * from another page in the group goes through the stale flag onEnterHomeRoute consumes.
 */
export function* initMainPage() {
  const tasks = yield* all([
    // Action handlers
    fork(onActionUpsertOrgSDO),
    fork(onActionUpsertOrgSDOSuccess),
    fork(onActionUpsertSDOFailure),

    fork(onActionFetchOrgSDOsByTDOsId),
    fork(onActionFetchSDOsBulkByTDOIds),

    fork(onActionDeleteOrgSDO),
    fork(onActionDeleteOrgSDOSuccess),
    fork(onActionDeleteSDOFailure),

    fork(onActionDeleteSelectedTDOs),
    fork(onActionSoftDeleteSelectedTDOs),
    fork(onActionUnlinkSelectedTDOs),

    fork(onActionDeleteSDOBulkFailure),
    fork(onActionSoftDeleteSDOBulkFailure),
    fork(onActionSoftDeleteTDOBulkFailure),
    fork(onActionDeleteTDOBulkFailure),

    fork(onActionFetchOrgSDOs),
    fork(onActionFetchSDOsSuccess),
    fork(onActionFetchSDOsFailure),

    fork(onActionFetchOrgTDOs),
    fork(onActionFetchTDOsSuccess),
    fork(onActionFetchTDOsFailure),

    fork(watchTrashTDOs),
    fork(watchTrashFolders),
    fork(watchPermanentDeleteActions),
    fork(watchRestoreActions),

    // On login, run a throttled background purge of expired trash items so they
    // don't linger until the user opens the Trash page. takeLatest detaches the
    // run so it never delays app startup.
    fork(function* watchTrashCleanupOnLogin() {
      yield* takeLatest(FETCH_USER_SUCCESS, runLoginTrashCleanup);
    }),

    fork(onActionFetchTDODetail),

    fork(onActionFetchNextBlock),

    fork(onReturnFromMediaDetails),

    fork(watchDeleteRedactCodeSaga),
    fork(watchSoftDeleteRedactCodeSaga),
    fork(watchSetRedactionCodeDeletedSaga),
    fork(watchRestoreRedactCodeSaga),
    fork(watchSaveRedactionCodeRequestSaga),

    // The admin superset (custom-status CRUD/reorder) rather than just the settings
    // watchers: ROUTE_REDACTION_STATUSES shares this saga so every opted-in route has one
    // saga identity and they never tear each other down (VE-26729). These are pure
    // watchers — no eager fetches — so the extra forks cost nothing on the other routes.
    fork(watchRedactionStatusAdministration),

    // Custom-lexicon administration (VE-24948 / U5). Same reasoning as the
    // statuses watchers above: ROUTE_LEXICONS shares this saga. These are
    // watchers with no eager LIST fetch — the Lexicons page dispatches its own
    // FETCH_LEXICONS on mount, since a preserved saga's start-up effects don't
    // re-fire on re-entry.
    //
    // The one exception is `probeLexiconAvailability`, a one-shot that runs
    // here precisely BECAUSE start-up effects don't re-fire: the side-nav entry
    // has to know whether this environment has a lexicon registry before anyone
    // visits the page, and it is the one consumer that cannot fetch for itself.
    // It resolves the registry (cached thereafter) and can only turn the
    // feature off; see `probeLexiconAvailability`.
    ...lexiconAdministrationSagas.map((watcher) => fork(watcher)),

    // External action handlers
    fork(onActionCreateTDOSuccess), // CREATE_TDO_SUCCESS
    fork(onActionAddTDOSuccess), // FETCH_TDO_SUCCESS
    fork(onActionRedactedFileCompleted),

    // Initialization handlers
    fork(pollTDOsRestartingOnFilterChange),
    fork(hackToCleanupDuplicateSDO),
    fork(trackOrgTasksChanges),
    fork(trackOrgJobsChanges),
    fork(caseMediaSaga),
    fork(fetchCountRedactionCodes),
    fork(defaultGlobalSettingsSaga),
    fork(watermarksSaga),
    fork(watchCheckRedactionCodeExists),

    fork(onEnterHomeRoute),
  ]);

  // Pay an owed Home refresh if we are landing on Home, because onEnterHomeRoute's watcher
  // is registered too late to see the ROUTE_HOME that started this saga.
  //
  // Do NOT just clear the flag here on the theory that a cold start refetches everything:
  // the STORE survives a route-saga restart, so a restart is only cold for the saga. The
  // folder tree in particular is not rebuilt — CaseMediaPage fetches root folders only
  // when none is resolved, and CaseFolders' restore bails out when columns are already
  // selected. Clearing without acting left a restored folder invisible until a hard reload
  // on the path Trash restore -> open a trashed file (ROUTE_FILE_IN_TRASH has no saga, so
  // this one is cancelled) -> back -> Home.
  const currentRouteType = yield* select(selectRouteType);
  if (currentRouteType === ROUTE_HOME.type) {
    // listAlreadyRefetched: the poller forked above has already dispatched the legacy
    // gallery's fetch by the time `all` returns (each fork runs to its first blocking
    // effect), so only the folder tree is genuinely owed here.
    //
    // The folderId guard holds here without being threaded through: refreshHomeIfStale
    // reads the route payload from the store. A cold start CAN carry one — the Case
    // Dashboard's case click and the document editor's close land on
    // ROUTE_HOME({folderId}) from routes whose saga is not this one.
    yield* refreshHomeIfStale({ listAlreadyRefetched: true });
  }

  // Skipped on a cold entry to the Statuses page itself: that page fetches on mount (it has
  // to, since a preserved saga's start-up effects don't re-fire on re-entry), so dispatching
  // here as well put two identical fetches in flight. Every OTHER route in the group still
  // needs this — the folder view renders status labels from it.
  if (currentRouteType !== ROUTE_REDACTION_STATUSES.type) {
    yield* put(actionFetchRedactionStatuses());
  }

  // Watermark library (VE-24693). Same shape as the statuses fetch above and skipped on
  // the Watermarks page for the same reason — that page fetches on mount, so dispatching
  // here too would put two identical fetches in flight. Every other route in the group
  // still needs it: the folder view's multi-select bar offers the library for batch apply.
  if (currentRouteType !== ROUTE_WATERMARKS.type) {
    // `pickableOnly` for the same reason as the editor route: the only consumer
    // outside the settings page is the folder view's batch-apply bar, which
    // offers `selectPickableWatermarks`. The settings page is excluded above
    // and fetches the whole library itself.
    yield* put(
      actionFetchWatermarks({ limit: 1000, offset: 0, pickableOnly: true })
    );
  }

  yield* take(LOGOUT);

  yield* cancel(tasks);
}

export function* initMediaDetailsPage() {
  // VE-25873 Unit 3 — resolve the file's parent request (and inject it into a
  // case-less URL; the redirect re-runs this saga with the case present, so
  // stop here), then warm the folder view for the exit in the background.
  const redirected = yield* resolveEditorReturnTarget(ROUTE_MEDIA_DETAILS);
  if (redirected) {
    return;
  }
  const tasks = yield* all([
    fork(warmEditorReturnTargetTree),
    fork(defaultGlobalSettingsSaga),
    fork(watchRedactionStatusSettings),
  ]);

  yield* put(actionFetchRedactionStatuses());
  // Load the page only once the statuses have settled, so lock-edit policy is
  // evaluated against real custom-status SDOs. Deep-linking straight into the
  // A/V editor otherwise raced the fetch and briefly allowed edit and save on
  // a locked custom status — the same gate the document editor's route saga
  // already has. Time-bounded, so a hung fetch delays the open, not blocks it
  // (VE-24546).
  yield* waitForRedactionStatusesSettled();
  const loadTask = yield* fork(loadMediaDetailsPage);

  yield* take(LOGOUT);

  yield* cancel([...tasks, loadTask]);
}

/** ****************************************************************************
 * INITIALIZES
 */

/**
 * Refreshes TDOs.
 */
// TODO: What should this return type really be?
export function* startPollingTDOsByPages(): Generator<any> {
  if (!(yield* select(userIsAuthenticated))) {
    yield* take(FETCH_USER_SUCCESS);
  }

  // yield* take('boot saga finished');
  const { itemsPerPage, currentPage } = yield* select(selectPagination);

  // prevent loading next page until initial loading is complete - in case returning from mediaDetails
  yield* put(
    Actions.actionUpdatePagination({
      hasMore: false,
    })
  );

  yield* put(
    Actions.actionFetchOrgSDOs({
      limit: currentPage * itemsPerPage + 1,
      offset: 0,
    })
  );

  const { err } = yield* race({
    ok: take(Actions.FETCH_ORGANIZATION_TDOS_SUCCESS),
    err: take(Actions.FETCH_ORGANIZATION_TDOS_FAILURE),
  });
  if (err) {
    console.error('ERROR');
    yield* delay(60_000);
    yield* startPollingTDOsByPages();
  }

  let count = 0;
  do {
    const { currentPage, itemsPerPage, loadingNextPage } =
      yield* select(selectPagination);

    const displaysMediaList = yield* routeDisplaysMediaList();

    // refresh all pages/tdos — but skip while the user is on a page that shows no media
    // (see MEDIA_LIST_ROUTE_TYPES). The initial fetch above still runs, so the first visit
    // to Home is already populated; this only suppresses the repeat cycles.
    if (count > 0 && !loadingNextPage && displaysMediaList) {
      // disable loading new page while refreshing - otherwise can enter race condition!
      // one we implement actionRefreshOrgSDOs this can go away
      yield* put(
        Actions.actionUpdatePagination({
          hasMore: false,
        })
      );

      // fetching SDO triggers reloading TDOs
      // TODO - implement a refresh version actionRefreshOrgSDOs that is more efficient
      yield* put(
        Actions.actionFetchOrgSDOs({
          limit: currentPage * itemsPerPage + 1,
          offset: 0,
        })
      );
      yield* take([
        Actions.FETCH_ORGANIZATION_TDOS_SUCCESS,
        Actions.FETCH_ORGANIZATION_TDOS_FAILURE,
      ]);
    }

    // check if more SDOs available for fetching.!
    if (!loadingNextPage) {
      // Get cumulative count of invalid sdo entries
      const { invalidSDOCount } = yield* select(selectPagination);

      const sdos = yield* select(selectSDOsArray);

      yield* put(
        Actions.actionUpdatePagination({
          hasMore: sdos.length + invalidSDOCount > currentPage * itemsPerPage,
        })
      );
    }

    // Delay polling. The clock free-runs while parked on a route that shows no media —
    // the cycle skips its fetch but still burns the full interval — so returning to Home
    // used to mean sitting out the remainder of a tick that had achieved nothing, up to a
    // whole POLLING_INTERVAL_MS of visibly stale data. (Before VE-26729 the route change
    // tore this saga down and restarted it, and the restart's own initial fetch made the
    // return look instant.) Cut the wait short on the way back instead.
    //
    // Only when this cycle actually skipped: ROUTE_HOME doubles as folder view's
    // jump-to-folder command, so waking on every one of them would refetch the whole org
    // list on every folder click — the opposite of what this ticket is for.
    if (displaysMediaList) {
      yield* delay(POLLING_INTERVAL_MS);
    } else {
      yield* race({
        timeout: delay(POLLING_INTERVAL_MS),
        returned: take([...MEDIA_LIST_ROUTE_TYPES]),
      });
    }
  } while (++count);
}

/** ****************************************************************************
 * ACTION HANDLERS
 */

/**
 * Somehow duplicate SDO are occasionally created. This cleans them up.
 */
export function* hackToCleanupDuplicateSDO() {
  yield* takeEvery(
    Actions.FETCH_ORGANIZATION_SDOS_SUCCESS,
    hackToCleanupDuplicateSDOHandle
  );
}

/**
 * Owns the single TDO polling loop and restarts it when the filter changes.
 *
 * `startPollingTDOsByPages` never returns — its `do { … delay } while (++count)` runs
 * forever. This used to be `takeEvery(CHANGE_FILTER, startPollingTDOsByPages)` alongside a
 * separate `fork(startPollingTDOsByPages)`, so every filter change added ANOTHER permanent
 * 60s loop, each issuing the org-wide SDO fetch plus a TDO fetch per record. That leak was
 * previously bounded only because the route saga was torn down on every navigation;
 * preserving the saga across the side-menu routes (VE-26729) would have made it permanent,
 * so the loop is now explicitly cancelled and restarted instead of stacked.
 */
export function* pollTDOsRestartingOnFilterChange() {
  let pollingTask = yield* fork(startPollingTDOsByPages);

  while (true) {
    yield* take(Actions.CHANGE_FILTER);
    yield* cancel(pollingTask);
    pollingTask = yield* fork(startPollingTDOsByPages);
  }
}

/**
 * VE-26729: refresh Home on entry when something off-route invalidated it.
 *
 * The nav drawer used to hard-reload the browser, so Home was rebuilt from scratch after
 * a Trash restore and the restored items simply appeared. It now navigates in-app, and
 * this saga is preserved across that navigation (routes sharing a saga are not re-forked),
 * so neither the reload nor the saga's start-up fetches happen. Without this, restored
 * files/folders would stay invisible on Home until the 60s poll came round.
 *
 * Only fires when the flag is set, so the common "just switching pages" case still costs
 * nothing — which is the point of the ticket.
 */
export function* onEnterHomeRoute() {
  // Wrapped rather than passed directly: refreshHomeIfStale takes options, not the action,
  // and takeLatest would otherwise hand the action in as the options object.
  yield* takeLatest(ROUTE_HOME.type, function* () {
    yield* refreshHomeIfStale();
  });
}

export function* refreshHomeIfStale({
  listAlreadyRefetched = false,
}: { listAlreadyRefetched?: boolean } = {}) {
  // ROUTE_HOME is overloaded: with a payload it is the in-page "jump to this folder"
  // command (search results, case dashboard, the document editor's close), not an entry to
  // Home. Refreshing there would clear the column chain the user is navigating INTO and
  // race the nav-to latch the same action sets. Those navigations start from a page that
  // already has live data, so there is nothing to refresh.
  //
  // Read from the STORE, not from a passed-in action. Both callers are ROUTE_HOME entries
  // and redux-saga emits to sagas only after the reducers have run, so the store already
  // reflects the action being handled — while an action parameter is something a caller
  // can forget to thread through. It was: initMainPage's cold-start call had no action to
  // pass, so this guard silently did not apply there, and a cold ROUTE_HOME({folderId})
  // (Case Dashboard case click, document editor close) cleared the chain it was opening.
  const { folderId } = (yield* select(selectCurrentRoutePayload)) as {
    folderId?: string;
  };
  if (folderId) {
    return;
  }

  const isStale = yield* select(selectHomeListStale);
  if (!isStale) {
    return;
  }

  // Cleared up front: a failed refetch must not leave the flag latched and refetch on
  // every subsequent Home entry for the rest of the session.
  yield* put(Actions.actionClearHomeListStale());

  const isFolderView = yield* select(selectEnableFolderView);

  if (isFolderView) {
    // Reproduce what the reload used to do: drop the column selection and refetch the
    // root folders. CaseFolders then restores the last-open chain from localStorage on
    // mount, exactly as it does after a real refresh.
    const limit = yield* select(selectDefaultLimit);
    const offset = yield* select(selectDefaultOffset);
    yield* put(actionClearAllSelectedFolders());
    yield* put(actionFetchFolderRequest({ limit, offset }));
    return;
  }

  // Skipped when the caller has just started the poller, whose own initial fetch is this
  // exact request (same limit, same offset). Firing it again would run two concurrent
  // org-wide SDO fetches, each fanning out to one TDO fetch per record — doubling the very
  // query this ticket exists to reduce — and this path also lacks the `hasMore: false`
  // guard startPollingTDOsByPages sets first to keep infinite scroll out of the race.
  if (listAlreadyRefetched) {
    return;
  }

  const { currentPage, itemsPerPage } = yield* select(selectPagination);
  // Same guard startPollingTDOsByPages sets before its own initial fetch, and needed for
  // the same reason: this refetch replaces the whole list from offset 0, so an infinite
  // scroll that fires while it is in flight would request a next page against pagination
  // the response is about to invalidate, duplicating or mispaging rows.
  yield* put(Actions.actionUpdatePagination({ hasMore: false }));
  yield* put(
    Actions.actionFetchOrgSDOs({
      limit: currentPage * itemsPerPage + 1,
      offset: 0,
    })
  );
}

export function* hackToCleanupDuplicateSDOHandle(
  { payload, meta }: ReturnType<typeof Actions.FETCH_ORGANIZATION_SDOS_SUCCESS>
  // {
  //   // TODO: why does this fail?
  //   // payload: {
  //   //   readonly structuredDataObjects: {
  //   //     readonly records: ReadonlyArray<StructuredDataObject>;
  //   //   };
  //   // };
  //   payload: {
  //     structuredDataObjects: {
  //       records: {
  //         id: string;
  //         createdDateTime: string;
  //         modifiedDateTime: string;
  //         data: any;
  //       }[];
  //     };
  //   };
  //   meta: { readonly variables: any };
  // }
) {
  const sdosToDelete = flatten(
    values(
      groupBy(payload.structuredDataObjects.records, (sdo) => sdo.data?.tdoId)
    )
      .filter((rs) => rs.length > 1)
      .map((rs) => rs.slice(-1))
  );
  if (sdosToDelete.length === 0) {
    return;
  }

  let del = true;
  for (const sdo of sdosToDelete) {
    // TODO: type the sdo.data object if possible
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    yield* put(Actions.actionDeleteOrgSDO({ tdoId: sdo.data.tdoId }));
    const { ok }: Record<string, any> = yield* race({
      ok: yield* take(Actions.DELETE_ORGANIZATION_SDO_SUCCESS),
      __: yield* take(Actions.DELETE_ORGANIZATION_SDO_FAILURE),
    });
    del &&= ok;
  }
  if (del) {
    yield* put(Actions.actionFetchOrgSDOs(meta.variables));
  }
}

function* onReturnFromMediaDetails() {
  yield* takeEvery(ROUTE_HOME, onReturnFromMediaDetailsHandle);
}

export function* onReturnFromMediaDetailsHandle() {
  const prevRoute = yield* select(selectPreviousRoute);
  if (prevRoute.type === ROUTE_MEDIA_DETAILS.type && prevRoute.payload?.tdoId) {
    yield* put(
      Actions.actionFetchOrgTDOs({
        // TODO: Can we type the previous route at all?
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        sdos: [{ tdoId: prevRoute.payload.tdoId }],
      })
    );
  }

  if (prevRoute.pathname === '/not-found') {
    const { currentPage, itemsPerPage } = yield* select(selectPagination);
    yield* put(
      Actions.actionFetchOrgSDOs({
        limit: currentPage * itemsPerPage + 1,
        offset: 0,
      })
    );
  }
}

/**
 * Handle Upsert SDO Actions.
 */
function* onActionUpsertOrgSDO() {
  yield* takeEvery(Actions.UPSERT_ORGANIZATION_SDO, function* ({ payload }) {
    yield* put(Services.upsertOrganizationSDO(payload));
  });
}

/**
 * Handles Upsert success.
 */
function* onActionUpsertOrgSDOSuccess() {
  yield* takeEvery(
    Actions.UPSERT_ORGANIZATION_SDO_SUCCESS,
    function* ({ payload }) {
      // use actionFetchOrgTDODetail instead of actionFetchOrgTDOs to prevent out of order finish with load new page or refresh in background
      yield* put(
        Actions.actionFetchOrgTDODetail({
          tdoId: payload.createStructuredData.data.tdoId,
        })
      );

      // check if now hasMore pages
      const { itemsPerPage, currentPage } = yield* select(selectPagination);
      const sdos = yield* select(selectSDOsArray);
      if (sdos.length > currentPage * itemsPerPage) {
        yield* put(
          Actions.actionUpdatePagination({
            hasMore: true,
          })
        );
      }

      // yield* put(
      //   Actions.actionFetchOrgTDOs({
      //     sdos: [{ tdoId: payload.createStructuredData.data.tdoId }],
      //   })
      // );
    }
  );
}

/**
 * Handles SDO upsert failure. Logs to console.
 */
function* onActionUpsertSDOFailure() {
  yield* takeEvery(
    Actions.UPSERT_ORGANIZATION_SDO_FAILURE,
    function* ({ payload }) {
      const intl = sagaIntl();
      console.error('Error while updating SDO from mainPage', payload);
      yield* put(
        enqueueSnackbar({
          message: intl.formatMessage({
            id: 'errorUpdatingMedia',
            defaultMessage: 'An error occurred while updating media.',
          }),
        })
      );
    }
  );
}

/**
 * Handle Deletes SDO Actions.
 */
function* onActionDeleteOrgSDO() {
  yield* takeEvery(Actions.DELETE_ORGANIZATION_SDO, onActionDeleteOrgSDOHandle);
}

export function* onActionDeleteOrgSDOHandle({
  payload,
}: {
  payload: { tdoId: TDOId };
}) {
  const sdo = yield* select(selectSDOById(payload.tdoId));
  const id = sdo?.id;

  if (id) {
    yield* put(
      Services.deleteOrganizationSDO({
        id,
        tdoId: payload.tdoId,
      })
    );
  }
}

/**
 * Handles SDO Delete Success Actions.
 */
function* onActionDeleteOrgSDOSuccess() {
  yield* takeEvery(
    Actions.DELETE_ORGANIZATION_SDO_SUCCESS,
    onActionDeleteOrgSDOSuccessHandle
  );
}

export function* onActionDeleteOrgSDOSuccessHandle(
  { meta }: ReturnType<typeof Actions.DELETE_ORGANIZATION_SDO_SUCCESS>
  // {
  //   meta: { readonly variables: { readonly tdoId: TDOId } };
  // }
) {
  yield* put(
    Actions.DELETE_ORGANIZATION_SDO_SUCCESS_UPDATE_STORE({
      tdoIds: [meta.variables.tdoId],
    })
  );
  yield* fillCurrentPageOrLoadNextPage(); // fill empty spots on current page
}

/**
 * Handles SDO Delete Failure. Logs to console.
 */
function* onActionDeleteSDOFailure() {
  yield* takeEvery(
    Actions.DELETE_ORGANIZATION_SDO_FAILURE,
    function* ({ payload }) {
      const intl = sagaIntl();
      console.error('Error while deleting SDO from mainPage', payload);
      yield* put(
        enqueueSnackbar({
          message: intl.formatMessage({
            id: 'errorRemovingMedia',
            defaultMessage: 'An error occurred while removing the media.',
          }),
        })
      );
    }
  );
}

/**
 * Handle Unlinking Selected TDOs.
 */
function* onActionUnlinkSelectedTDOs() {
  yield* takeEvery(
    Actions.UNLINK_SELECTED_TDOS,
    onActionUnlinkSelectedTDOsHandle
  );
}

export function* onActionUnlinkSelectedTDOsHandle() {
  const tdoIds = yield* select(selectSelectedTDOsArray);
  const sdos = (yield* all(
    tdoIds.map((tdoId: TDOId) => select(selectSDOById(tdoId)))
  )).filter(notUndefined);
  const sdoIds = sdos.map((sdo: { id: string }) => sdo.id);
  yield* put(
    Services.deleteSDOBulk({
      sdoIds,
    })
  );
  const { err } = yield* race({
    ok: take(Actions.DELETE_SDO_BULK_SUCCESS),
    err: take(Actions.DELETE_SDO_BULK_FAILURE),
  });

  // TODO: In the case of error this appears to put SDOs into this array??
  let tdoIdsUnlinked: any[];
  if (err) {
    const failedSDOIds = err.payload.map((error) =>
      get(error, 'data.objectId')
    );
    const sdoIdsDeleted = without(sdoIds, ...failedSDOIds);
    tdoIdsUnlinked = yield* all(
      sdoIdsDeleted.map((sdoId: string) => select(selectSDOBySDOId(sdoId)))
    );
  } else {
    tdoIdsUnlinked = tdoIds;
  }
  if (tdoIdsUnlinked.length === 0) {
    return;
  }
  if (tdoIdsUnlinked.length === tdoIds.length) {
    yield* put(
      enqueueSnackbar({
        message: 'Unlinking from CMS was successful.',
      })
    );
  }
  yield* put(
    Actions.DELETE_ORGANIZATION_SDO_SUCCESS_UPDATE_STORE({
      // TODO: Fix the type of tdoIdsUnlinked
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      tdoIds: tdoIdsUnlinked,
    })
  );
  yield* put(Actions.actionClearSelectedTDOs());
  yield* fillCurrentPageOrLoadNextPage(); // fill empty spots on current page
}

/**
 * Handle Deleting Selected TDOs.
 */
function* onActionDeleteSelectedTDOs() {
  yield* takeEvery(
    Actions.DELETE_SELECTED_TDOS,
    onActionDeleteSelectedTDOsHandle
  );
}

export function* onActionDeleteSelectedTDOsHandle() {
  const tdoIds = yield* select(selectSelectedTDOsArray);
  yield* put(
    Services.deleteTDOBulk({
      tdoIds,
    })
  );
  yield* put(Actions.actionClearSelectedTDOsAndTagDeleting());
  // const {
  //   err,
  // }: {
  //   err: { readonly payload: { readonly objectId: string }[] } | undefined;
  // } =
  const { err } = yield* race({
    ok: take(Actions.DELETE_TDO_BULK_SUCCESS),
    err: take(Actions.DELETE_TDO_BULK_FAILURE),
  });

  let tdoIdsDeleted: TDOId[];
  if (err) {
    const failedTDOIds = err.payload.map((error) =>
      get(error, 'data.objectId')
    );
    if (includes(failedTDOIds, undefined)) {
      // edge case: unable to determine which TDOs were deleted if any errors are missing objectId
      // may want to force a harder refresh for orphan SDO cleanup to kick in
      tdoIdsDeleted = [];
    } else {
      tdoIdsDeleted = without(tdoIds, ...failedTDOIds);
    }
  } else {
    tdoIdsDeleted = tdoIds;
  }
  if (tdoIdsDeleted.length === 0) {
    return;
  }
  const sdos = yield* all(
    tdoIdsDeleted.map((tdoId) => select(selectSDOById(tdoId)))
  );
  const sdoIds = sdos.filter(notUndefined).map((sdo: { id: string }) => sdo.id);
  yield* put(
    Services.deleteSDOBulk({
      sdoIds,
    })
  );

  yield* race({
    ok: take(Actions.DELETE_SDO_BULK_SUCCESS),
    err: take(Actions.DELETE_SDO_BULK_FAILURE),
  });

  if (tdoIdsDeleted.length === tdoIds.length) {
    yield* put(
      enqueueSnackbar({
        message: 'Deletion successful.',
      })
    );
  }
  yield* put(
    Actions.DELETE_ORGANIZATION_SDO_SUCCESS_UPDATE_STORE({
      tdoIds: tdoIdsDeleted,
    })
  );
  yield* fillCurrentPageOrLoadNextPage(); // fill empty spots on current page
}

/**
 * Handle Soft Deleting Selected TDOs.
 */
function* onActionSoftDeleteSelectedTDOs() {
  yield* takeEvery(
    Actions.SOFT_DELETE_SELECTED_TDOS,
    onActionSoftDeleteSelectedTDOsHandle
  );
}

export function* onActionSoftDeleteSelectedTDOsHandle() {
  const tdoIds = yield* select(selectSelectedTDOsArray);
  const selectedTdosToDelete = yield* all(
    (tdoIds || []).map((tdoId: TDOId) => select(selectTDOById(tdoId)))
  );
  const trashRetentionDays = yield* select(selectTrashRetentionDays);

  yield* put(
    Services.softDeleteTDOBulk({
      tdos: selectedTdosToDelete.filter((tdo) => !!tdo),
      trashRetentionDays,
    })
  );
  yield* put(Actions.actionClearSelectedTDOsAndTagDeleting());
  const { err } = yield* race({
    ok: take(Actions.SOFT_DELETE_TDO_BULK_SUCCESS),
    err: take(Actions.SOFT_DELETE_TDO_BULK_FAILURE),
  });

  let tdoIdsDeleted: TDOId[];
  if (err) {
    const failedTDOIds = err.payload.map((error) =>
      get(error, 'data.objectId')
    );
    if (includes(failedTDOIds, undefined)) {
      tdoIdsDeleted = [];
    } else {
      tdoIdsDeleted = without(tdoIds, ...failedTDOIds);
    }
  } else {
    tdoIdsDeleted = tdoIds;
  }
  if (tdoIdsDeleted.length === 0) {
    return;
  }

  const tdoById = new Map(
    selectedTdosToDelete
      .filter(notUndefined)
      .map((tdo) => [tdo.id, tdo] as const)
  );
  const trashFolderId = yield* call(ensureTrashFolderId);
  if (!trashFolderId) {
    return;
  }

  const tdoIdsMoved: TDOId[] = [];
  for (const tdoId of tdoIdsDeleted) {
    const oldFolderId = tdoById.get(tdoId)?.folders?.[0]?.id;

    const moveToTrashActionTypes = [
      'organization/move_tdo_to_trash_init',
      'organization/move_tdo_to_trash_success',
      'organization/move_tdo_to_trash_failure',
    ] as const;
    const moveVariables = {
      tdoId,
      newFolderId: trashFolderId,
      oldFolderId,
    };
    const moveQuery = isEmpty(oldFolderId)
      ? fileTdoWithCaseQuery
      : moveTdoQuery;

    yield* put(
      CaseMediaServices.invokeGqlCall<
        FileTdoWithCaseQueryResponse | MoveTdoQueryResponse
      >(moveQuery, moveVariables, moveToTrashActionTypes)
    );

    const moveResult: { type: string } = yield* take([
      moveToTrashActionTypes[1],
      moveToTrashActionTypes[2],
    ]);
    if (moveResult.type === moveToTrashActionTypes[2]) {
      yield* put(
        enqueueSnackbar({
          message: `Failed to move TDO ${tdoId} to trash.`,
          variant: 'error',
        })
      );
    } else {
      tdoIdsMoved.push(tdoId);
    }
  }

  yield* put(
    actionUpdateStoreSoftDeletedTdos({
      tdoIds: [...tdoIdsMoved],
    })
  );
  if (tdoIdsMoved.length === tdoIds.length) {
    // Match the CaseMedia batch-delete snackbar: the wording must reflect the
    // retention setting. trashRetentionDays > 0 moves files to trash
    // (recoverable); === 0 deletes them permanently. (VE-25226)
    const intl = sagaIntl();
    const isSoftDelete = trashRetentionDays > 0;
    const successMessage = isSoftDelete
      ? intl.formatMessage(
          {
            id: 'batchTrashFilesSuccess',
            defaultMessage:
              '{count, plural, one {# file moved to trash.} other {# files moved to trash.}}',
          },
          { count: tdoIdsMoved.length }
        )
      : intl.formatMessage(
          {
            id: 'batchDeleteFilesSuccess',
            defaultMessage:
              '{count, plural, one {# file deleted.} other {# files deleted.}}',
          },
          { count: tdoIdsMoved.length }
        );
    yield* put(
      enqueueSnackbar({ message: successMessage, variant: 'success' })
    );
  }
  yield* fillCurrentPageOrLoadNextPage(); // fill empty spots on current page
}

/**
 * Handles SDO Bulk Delete Failure. Logs to console.
 */
function* onActionDeleteSDOBulkFailure() {
  yield* takeEvery(Actions.DELETE_SDO_BULK_FAILURE, function* ({ payload }) {
    const intl = sagaIntl();
    console.error('Error while deleting SDOs from mainPage', payload);
    yield* put(
      enqueueSnackbar({
        message: intl.formatMessage({
          id: 'errorUnlinkingFiles',
          defaultMessage:
            'An error occurred while unlinking one or more files.',
        }),
      })
    );
  });
}

/**
 * Handles SDO Bulk Soft Delete Failure. Logs to console.
 */
function* onActionSoftDeleteSDOBulkFailure() {
  const intl = sagaIntl();
  yield* takeEvery(Actions.SOFT_DELETE_SDO_BULK_FAILURE, function* () {
    yield* put(
      enqueueSnackbar({
        message: intl.formatMessage({
          id: 'errorRemovingFiles',
          defaultMessage: 'An error occurred while removing one or more files.',
        }),
      })
    );
  });
}
/**
 * Handles  TDO Bulk Soft Delete Failure. Logs to console.
 */
function* onActionSoftDeleteTDOBulkFailure() {
  const intl = sagaIntl();
  yield* takeEvery(Actions.SOFT_DELETE_TDO_BULK_FAILURE, function* () {
    yield* put(
      enqueueSnackbar({
        message: intl.formatMessage({
          id: 'errorRemovingFiles',
          defaultMessage: 'An error occurred while removing one or more files.',
        }),
      })
    );
  });
}
/**
 * Handles SDO Bulk Delete Failure. Logs to console.
 */
function* onActionDeleteTDOBulkFailure() {
  yield* takeEvery(Actions.DELETE_TDO_BULK_FAILURE, function* ({ payload }) {
    const intl = sagaIntl();
    console.error('Error while deleting TDOs from mainPage', payload);
    yield* put(
      enqueueSnackbar({
        message: intl.formatMessage({
          id: 'errorRemovingFiles',
          defaultMessage: 'An error occurred while removing one or more files.',
        }),
      })
    );
  });
}

/**
 * Handles Fetch SDOs Actions.
 */
function* onActionFetchOrgSDOs() {
  const chan = yield* actionChannel(
    Actions.FETCH_ORGANIZATION_SDOS,
    buffers.expanding(10)
  );
  yield* takeEvery(chan, function* ({ payload }) {
    yield* put(Services.fetchOrganizationSDOs(payload));
  });
}

/**
 * Handles FETCH_ORGANIZATION_SDOS_SUCCESS. Kicks off actionFetchOrgTDOs() action.
 */
function* onActionFetchSDOsSuccess() {
  yield* takeEvery(
    Actions.FETCH_ORGANIZATION_SDOS_SUCCESS,
    onActionFetchSDOsSuccessHandle
  );
}

export function* onActionFetchSDOsSuccessHandle(
  action: ReturnType<typeof Actions.FETCH_ORGANIZATION_SDOS_SUCCESS>
) {
  const { structuredDataObjects } = action.payload;

  // VE-24955: advance the server-offset high-water mark by the raw record count
  // (including trashed/invalid SDOs that later get dropped from the store), so
  // the next page fetch resumes past what the server already returned instead
  // of re-requesting the same rows forever.
  yield* put(
    Actions.actionUpdatePagination({
      sdoOffset:
        (action.meta?.variables?.offset ?? 0) +
        (structuredDataObjects?.records.length ?? 0),
    })
  );

  if (structuredDataObjects?.records.length > 0) {
    const sdos = (structuredDataObjects?.records || [])
      .filter((r) => !isEmpty(r.data?.tdoId))
      // TODO: type sdo.data
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      .map((s) => ({ tdoId: s.data.tdoId }));
    // remove invalid sdos (missing tdoId)
    const invalidSdos = (structuredDataObjects?.records || [])
      .filter((r) => !r.data?.tdoId)
      .map((sdo: { id: string }) => sdo.id);

    if (invalidSdos?.length > 0) {
      yield* put(
        Actions.actionUpdatePagination({
          invalidSDOCount: invalidSdos.length,
        })
      );
    }
    if (sdos.length > 0) {
      yield* put(Actions.actionFetchOrgTDOs({ sdos }));
    }
  } else {
    yield* put(Actions.actionFetchOrgTDOsSuccess({}));
  }
}

/**
 * Handles FETCH_ORGANIZATION_SDOS_FAILURE.
 */
function* onActionFetchSDOsFailure() {
  yield* takeEvery(
    Actions.FETCH_ORGANIZATION_SDOS_FAILURE,
    function* ({ payload }) {
      const intl = sagaIntl();
      console.error('Error while fetching SDOs from mainPage', payload);
      yield* put(
        enqueueSnackbar({
          message: intl.formatMessage({
            id: 'errorPreloadingMedia',
            defaultMessage:
              'An error occurred while pre-loading the media list.',
          }),
        })
      );
    }
  );
}

function* onActionFetchOrgSDOsByTDOsId() {
  yield* takeEvery(
    Actions.FETCH_ORGANIZATION_SDOS_BY_ID,
    function* ({ payload }) {
      yield* put(Services.fetchOrganizationSDOsByTDOsId(payload));
    }
  );
}

/**
 * Handles Fetch TDOs Actions.
 */
function* onActionFetchOrgTDOs() {
  const chan = yield* actionChannel(
    Actions.FETCH_ORGANIZATION_TDOS,
    buffers.expanding(10)
  );
  yield* takeEvery(chan, function* ({ payload }) {
    yield* put(Services.fetchOrganizationTDOs(payload));
  });
}

/**
 * Handles FETCH_ORGANIZATION_TDOS_SUCCESS. Kicks off stop polling actions.
 */
function* onActionFetchTDOsSuccess() {
  yield* takeEvery(
    Actions.FETCH_ORGANIZATION_TDOS_SUCCESS,
    onActionFetchTDOsSuccessHandle
  );
}

/**
 * How long a run started in this tab may go without an engine task of its own
 * before the latch stops shielding it (VE-30516).
 *
 * Job creation takes seconds. The ceiling exists only so that a run whose job
 * never materialised — a failed `createJob`, a worker that exited early —
 * cannot shield its flag for ever, which would be the stuck spinner all over
 * again. Past it, the latch judges by the newest task as it always did.
 */
const JOB_REGISTRATION_GRACE_MS = 120_000;

/**
 * Does the newest blur task belong to a run that ended BEFORE the one currently
 * flagged as running started?
 *
 * The latch judges by `maxBy(createdDateTime)`, which during the gap between a
 * REDACT FILE click and the new engine task becoming queryable still returns the
 * PREVIOUS run's task. If that one is terminal the release branch fires: it
 * clears the flag and the version mid-run, and on `complete` announces "File
 * redacted successfully" for a redaction that has only just begun.
 *
 * `startedAt` is set only by the editor path that starts a run, so a flag raised
 * by the latch itself (a run started elsewhere, or found in flight on load)
 * carries none and is judged exactly as before.
 */
const taskPredatesRun = (
  taskCreatedAt: string | undefined,
  entry: { startedAt?: string } | undefined
): boolean => {
  const startedAt = entry?.startedAt;
  if (!startedAt || !taskCreatedAt) {
    return false;
  }
  const startedMs = new Date(startedAt).getTime();
  const taskMs = new Date(taskCreatedAt).getTime();
  if (Number.isNaN(startedMs) || Number.isNaN(taskMs)) {
    return false;
  }
  return (
    taskMs <= startedMs && Date.now() - startedMs < JOB_REGISTRATION_GRACE_MS
  );
};

export function* onActionFetchTDOsSuccessHandle(
  action: ReturnType<typeof Actions.FETCH_ORGANIZATION_TDOS_SUCCESS>
) {
  // check redact task to push snack bar
  const tdos = get(action, 'payload');
  const { redactEngineId, legacyRedactEngineId } =
    yield* select(selectConfigEngines);
  const redactFiles = yield* select(selectRedactFiles);
  for (const key in tdos) {
    if (Object.prototype.hasOwnProperty.call(tdos, key)) {
      const tdo = tdos[key]!;
      // VE-26782: skip the server-injected `warnings` array that can sit
      // alongside the `id<tdoId>` aliases in the mapTDOs response.
      if (!isSummaryTDORecord(tdo)) {
        continue;
      }
      const tasks = get(tdo, 'tasks.records', []);
      const redactTask = maxBy(
        tasks.filter(
          (t: { engineId: string }) =>
            t.engineId === redactEngineId || t.engineId === legacyRedactEngineId
        ),
        'createdDateTime'
      );
      // VE-30516: ask whether the task is in flight, and treat everything else
      // as over. This used to be `!== 'complete'`, which read a
      // `failed`/`cancelled`/`aborted` task as a redaction in progress; since
      // the release fired only on `complete`, a file whose blur job only ever
      // failed was pinned on "Redacting…" — re-pinned, in fact, on every
      // dashboard fetch that reaches here.
      //
      // The two branches are complements of one predicate rather than two
      // independent lists, so no status can fall between them and leave the
      // flag neither set nor released — which is how the first cut of this fix
      // reintroduced the same stuck spinner for `paused` and friends.
      //
      // `redactTask &&` on the release is load-bearing, not defensive: image
      // and document burn-ins run in the browser and create no engine task
      // (VE-28061), so their `running` flag must not be cleared by a dashboard
      // fetch that simply finds no blur task on the file.
      const redactTaskStatus = redactTask?.status;
      if (redactTask && isRedactTaskInFlight(redactTaskStatus)) {
        yield* put(redactingFile(tdo.id));
      } else if (
        redactTask &&
        redactFiles[tdo.id]?.status === 'running' &&
        !taskPredatesRun(redactTask.createdDateTime, redactFiles[tdo.id])
      ) {
        if (isRedactTaskComplete(redactTaskStatus)) {
          // Success: announce it. `REDACTED_FILE_COMPLETED` is the mediaDetails
          // action, which drives the "File redacted successfully" snackbar and
          // the row refresh as well as releasing the flag.
          yield* put(
            REDACTED_FILE_COMPLETED({ tdoId: tdo.id, documentName: tdo.name })
          );
        } else {
          // Failed, cancelled or aborted: release the flag and nothing else.
          // `redactFileCompleted` is the redactFile duck's own action — the
          // near-identical name is unfortunate — so this clears the status
          // WITHOUT the success snackbar, which would otherwise congratulate
          // the user on a redaction that did not happen. The surfaces fall
          // back to the durable engine-task status, which reports the failure.
          yield* put(redactFileCompleted(tdo.id));
        }
      }
    }
  }

  // The only thing keeping a trashed file off THIS grid — the legacy dashboard;
  // the folder view filters separately in the casemedia reducer. Trashing does
  // not delete the file's SDO, so the server re-delivers it on every refresh and
  // this is what re-removes it. Anything it fails to recognise comes back as a
  // tile and stays there (VE-31036 — see isTdoFiledInTrash for the v2 and
  // multi-folder shapes this used to miss).
  const tdoIdsInTrashFolder = values(tdos)
    .filter(isSummaryTDORecord)
    .filter((tdo) => isTdoFiledInTrash(tdo.folders))
    .map((tdo) => tdo.id);

  if (tdoIdsInTrashFolder.length > 0) {
    yield* put(
      Actions.DELETE_ORGANIZATION_SDO_SUCCESS_UPDATE_STORE({
        tdoIds: tdoIdsInTrashFolder,
      })
    );

    yield* fillCurrentPageOrLoadNextPage();
  }

  yield { type: 'NOOP' };
}

/**
 * Handles FETCH_ORGANIZATION_TDOS_FAILURE.
 * If TDO(s) are not found, remove from SDO list.
 */
function* onActionFetchTDOsFailure() {
  yield* takeEvery(
    Actions.FETCH_ORGANIZATION_TDOS_FAILURE,
    onActionFetchTDOsFailureHandle
  );
}

export function* onActionFetchTDOsFailureHandle({
  payload,
}: {
  payload: {
    readonly name: string;
    readonly data: { readonly objectId: string };
  }[];
}) {
  console.error('Error while fetching TDOs from mainPage', payload);
  let pullPageAgain = false;
  if (Array.isArray(payload)) {
    for (const err of payload) {
      if (err?.name === 'not_found' && err.data?.objectId) {
        yield* put(Actions.actionDeleteOrgSDO({ tdoId: err.data.objectId }));
        yield* take([
          Actions.DELETE_ORGANIZATION_SDO_SUCCESS,
          Actions.DELETE_ORGANIZATION_SDO_FAILURE,
        ]);
        pullPageAgain = true;
      }
    }
  }
  if (pullPageAgain) {
    yield* fillCurrentPageOrLoadNextPage();
  }
  yield { type: 'NOOP' };
}

/**
 * Handles Fetch TDO Detail Actions.
 */
function* onActionFetchTDODetail() {
  yield* takeEvery(
    Actions.FETCH_ORGANIZATION_TDO_BY_ID,
    function* ({ payload }) {
      yield* put(Services.fetchOrganizationTDOById(payload));
    }
  );
}

/**
 * Handles FETCH_NEXT_BLOCK action by pulling next page.
 */
function* onActionFetchNextBlock() {
  const chan = yield* actionChannel(
    Actions.FETCH_NEXT_BLOCK,
    buffers.dropping(0)
  );
  yield* takeEvery(chan, function* () {
    yield* fillCurrentPageOrLoadNextPage();
  });
}

/**
 * Watches for new TDOs. Adds the applicable SDO.
 */
function* onActionCreateTDOSuccess() {
  yield* takeEvery(CREATE_TDO_SUCCESS, onActionCreateTDOSuccessHandle);
}

export function* onActionCreateTDOSuccessHandle({
  payload,
  meta,
}: ReturnType<typeof CREATE_TDO_SUCCESS>) {
  if (isNil(meta?.variables?.input?.parentFolderId)) {
    const tdoId = payload.createTDO.id;
    const statusSettings = yield* select(selectRedactionStatusSettings);
    const initialFields =
      getInitialWellKnownFileRedactionUpsertFields(statusSettings);
    yield* put(
      Actions.actionUpsertSDO({
        data: {
          tdoId,
          ...initialFields,
        },
      })
    );
    yield* race({ _: take(CREATE_ENGINE_JOB_SUCCESS), __: delay(10_000) }); // this isn't actually necessary
  }
}

/**
 * Watches for adding existing TDOs via external link. Adds the applicable SDO.
 */
function* onActionAddTDOSuccess() {
  yield* takeEvery(FETCH_TDO_SUCCESS, onActionAddTDOSuccessHandle);
}

export function* onActionAddTDOSuccessHandle({
  payload: { temporalDataObject },
}: {
  payload: { temporalDataObject: DetailTDO };
}) {
  const { id } = temporalDataObject;
  const intl = sagaIntl();
  const sdo = yield* select(selectSDOById(id));
  if (sdo) {
    return;
  }
  yield* put(Actions.actionFetchOrgSDOsByTDOsId({ tdoId: id }));
  const { ok, err } = yield* race({
    ok: take(Actions.FETCH_ORGANIZATION_SDOS_BY_ID_SUCCESS),
    err: take(Actions.FETCH_ORGANIZATION_SDOS_BY_ID_FAILURE),
  });
  if (ok) {
    if (ok.payload?.structuredDataObjects?.records?.length) {
      return;
    }

    yield* put(updateStatusOnDetails());
    const statusSettings = yield* select(selectRedactionStatusSettings);
    const initialFields =
      getInitialWellKnownFileRedactionUpsertFields(statusSettings);
    yield* put(
      Actions.actionUpsertSDO({
        data: {
          tdoId: id,
          ...initialFields,
        },
      })
    );

    // VE-26730 (BR-7c): baseline status-history row for a TDO first seen here.
    // Uses the file's CURRENT tag status when it has one (a pre-existing file
    // must not get a fabricated "To Do" row) and flags it isSystem — this is a
    // system-generated baseline, not a user transition. Best-effort like every
    // history write; inert when the feature is unprovisioned.
    const statusTag = temporalDataObject.details?.tags?.find(
      (tag) => tag?.value === IN_REDACTION_TAG_KEY
    );
    const currentFields = statusTag
      ? deriveStatusFieldsFromTag(statusTag)
      : null;
    yield* put(
      actionRecordStatusHistory({
        tdoId: id,
        ...(currentFields ?? initialFields),
        isSystem: true,
        // A file that already carried a status was live before Redact saw it,
        // so this row is a join point and everything before it is unrecorded.
        // With no status tag there was nothing before it to miss.
        origin: currentFields ? 'adopted' : 'created',
      })
    );

    if (process.env.NODE_ENV === 'development') {
      console.error(
        'External File Added: upsertOrganizationSDO updated without ID',
        sdo,
        ok
      );
      // alert(
      //   'upsertOrganizationSDO attempted to update without ID. Check console.'
      // );
    }
  } else {
    console.error(
      'onActionAddTDOSuccess() FETCH_ORGANIZATION_SDOS_BY_ID_FAILURE',
      err
    );
    yield* put(
      enqueueSnackbar({
        message: intl.formatMessage({
          id: 'errorAddingMedia',
          defaultMessage: 'An error occurred while adding the media.',
        }),
      })
    );
  }
}

/**
 * Watches for REDACTED_FILE_COMPLETED action to refresh TDO on homepage.
 */
function* onActionRedactedFileCompleted() {
  yield* takeEvery(
    REDACTED_FILE_COMPLETED,
    onActionRedactedFileCompletedHandle
  );
}

export function* onActionRedactedFileCompletedHandle({
  payload: { tdoId },
}: {
  payload: { tdoId: TDOId };
}) {
  const currentRouteType = yield* select(selectRouteType);

  if (currentRouteType === ROUTE_HOME.type) {
    yield* put(
      Actions.actionFetchOrgTDODetail({
        tdoId: tdoId,
      })
    );
  }
}

/** ****************************************************************************
 * HELPERS
 */

/**
 * Fill current page if empty spots. Otherwise load next page if currentPage is full
 */
export const fillCurrentPageOrLoadNextPage = function* () {
  const { itemsPerPage, sdoOffset } = yield* select(selectPagination);

  const sdosBefore = yield* select(selectSDOsArray);
  const numFilledPages = Math.floor(sdosBefore.length / itemsPerPage);
  // VE-24955: page against the count of records already consumed from the
  // server (sdoOffset), NOT the number of SDOs currently in the store. Trashed
  // (and invalid) SDOs are dropped from the store after fetch, so using the
  // store size as the offset would re-request the same trashed rows forever.
  const offset = sdoOffset;
  const limit = (numFilledPages + 1) * itemsPerPage - sdosBefore.length + 1; // attempt to fill an entire page +1 to check if more
  yield* put(
    Actions.actionUpdatePagination({
      hasMore: false,
      loadingNextPage: true,
    })
  );

  yield* put(Actions.actionFetchOrgSDOs({ limit, offset }));
  yield* take([
    Actions.FETCH_ORGANIZATION_TDOS_SUCCESS,
    Actions.FETCH_ORGANIZATION_TDOS_FAILURE,
  ]);
  const sdos = yield* select(selectSDOsArray);

  const { sdoOffset: sdoOffsetAfter } = yield* select(selectPagination);

  // update currentPage
  let currentPage = numFilledPages;
  if (sdos.length > numFilledPages * itemsPerPage) {
    currentPage = currentPage + 1; // must have added a partial or full page
  }

  yield* put(
    Actions.actionUpdatePagination({
      currentPage: Math.max(currentPage, 1), // currentPage must be at least 1
      loadingNextPage: false,
      // More pages exist only if the server returned a full block. Measuring
      // records returned (not net-new store items) means trash filtered out
      // client-side no longer makes a full block read as "nothing left".
      hasMore: sdoOffsetAfter - offset >= limit,
    })
  );
};

/** THESE FUNCTIONS ARE NOT USED / DEPRECATED
export const fetchSDOsByPage = function* (page = 0) {
  // currently this function only works properly for page 0.
  const { itemsPerPage } = yield* select(
    selectPagination
  );
  const limit = itemsPerPage + 1;
  const offset = page * itemsPerPage;
  yield* put(Actions.actionFetchOrgSDOs({ limit, offset }));
};

export const fetchSDOsByLastPage = function* () {
  const { currentPage, invalidSDOCount } = yield* select(selectPagination);
  yield* fetchSDOsByPage(currentPage);
  const { ok } = yield* race({
    ok: take(Actions.FETCH_ORGANIZATION_SDOS_SUCCESS),
    _: take(Actions.FETCH_ORGANIZATION_SDOS_FAILURE),
  });
  if (ok) {
    const sdos = ok.payload.structuredDataObjects.records;
    const { itemsPerPage } = yield* select(selectPagination);
    yield* put(
      Actions.actionUpdatePagination({
        hasMore: sdos.length + invalidSDOCount > itemsPerPage,
      })
    );
  }
};
*/

export function* trackOrgTasksChanges() {
  let since = new Date().toUTCString();

  while (true) {
    const isAuthenticated = yield* select(userIsAuthenticated);
    const displaysMediaList = yield* routeDisplaysMediaList();

    // `since` deliberately does not advance while skipping, so the first fetch after the
    // user returns covers the whole gap rather than losing the changes in it.
    if (!isAuthenticated || !displaysMediaList) {
      // Wake early when the user comes back to a media route rather than sitting out the
      // rest of an interval that skipped — see the same race in startPollingTDOsByPages.
      // Not when merely unauthenticated: no route change fixes that.
      if (displaysMediaList) {
        yield* delay(TRACK_ORG_TASKS_CHANGES_INTERVAL);
      } else {
        yield* race({
          timeout: delay(TRACK_ORG_TASKS_CHANGES_INTERVAL),
          returned: take([...MEDIA_LIST_ROUTE_TYPES]),
        });
      }
      continue;
    }

    yield* put(Services.fetchOrganizationTasks({ since }));
    const newSince = new Date().toUTCString();

    const { ok, err } = yield* race({
      ok: take(FETCH_ORGANIZATION_TASKS_SUCCESS),
      err: take(FETCH_ORGANIZATION_TASKS_FAILURE),
    });

    if (!err) {
      since = newSince;
    }

    if (ok) {
      const tasks = ok.payload?.tasks?.records || [];

      if (tasks.length) {
        yield* put(updateOrgTdosTasksAction(tasks));
      }
    }

    yield* delay(TRACK_ORG_TASKS_CHANGES_INTERVAL);
  }
}

export function* trackOrgJobsChanges() {
  let since = new Date().toUTCString();

  while (true) {
    const isAuthenticated = yield* select(userIsAuthenticated);
    const displaysMediaList = yield* routeDisplaysMediaList();

    // See trackOrgTasksChanges: `since` stays put across skipped cycles, and a return to a
    // media route wakes the loop early instead of waiting out a skipped interval.
    if (!isAuthenticated || !displaysMediaList) {
      if (displaysMediaList) {
        yield* delay(TRACK_ORG_JOBS_CHANGES_INTERVAL);
      } else {
        yield* race({
          timeout: delay(TRACK_ORG_JOBS_CHANGES_INTERVAL),
          returned: take([...MEDIA_LIST_ROUTE_TYPES]),
        });
      }
      continue;
    }

    yield* put(Services.fetchOrganizationJobs({ since }));
    const newSince = new Date().toUTCString();

    const { ok, err } = yield* race({
      ok: take(FETCH_ORGANIZATION_JOBS_SUCCESS),
      err: take(FETCH_ORGANIZATION_JOBS_FAILURE),
    });

    if (!err) {
      since = newSince;
    }

    if (ok) {
      const jobs = ok.payload?.jobs?.records || [];

      if (jobs.length) {
        yield* put(updateOrgTdosJobsAction(jobs));
      }
    }

    yield* delay(TRACK_ORG_JOBS_CHANGES_INTERVAL);
  }
}
